import React, { memo, Suspense, useCallback, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { HStack } from '../../../../../../ui/Stack'
import { Modal } from 'antd'
import {
  EditOutlined,
  PlusOutlined,
  CloseOutlined,
  CalendarOutlined,
  CheckOutlined,
} from '@ant-design/icons'
import { ModalCreateTask } from '../../../../../../views/modal-create-task-new'
import { ModalAddNote } from '../../../../../../views/modal-add-notes-new'
import { MeasurementEditDateModal } from '../../measurement-edit-date-modal'
import { ModalCancel } from '../../../../../../views/modal-cancel'
import { getMeasurementForm } from '../../../../../../store/measurement/action-creators'
import { getTimeLineData } from '../../../../../../store/timeline/action-creators'
import { useSelector } from 'react-redux'
import {
  getVisibleFormData,
  getMeasurementFormData,
  getMeasurementParentId,
} from '../../../selectors'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../../components/v2/card-actions'
import { HSteps } from '../../../../../../ui/Steps'

interface MeasurementFormsHeaderProps {
  id: string | number
  className?: string
  stage?: number
  isFinished?: boolean
  toOrder?: () => void
  toClaim?: () => void
  toFinish?: () => void
  showTitle?: boolean
}

const titlesButton = [
  'Взять в работу',
  'Зафиксировать приезд',
  'Замер произведен',
  'Договор подписан',
  'Выполнено',
]

export const MeasurementFormsHeader: React.FC<MeasurementFormsHeaderProps> = memo(
  (props: MeasurementFormsHeaderProps) => {
    const { id, stage = 0, toOrder, toFinish } = props
    const isDisabledButtons = id === 'new' || stage === 5
    const [isOpenModalCreateTask, setIsOpenModalCreateTask] = useState<boolean>(false)
    const [isOpenModalAddNote, setIsOpenModalAddNote] = useState<boolean>(false)
    const [isOpenModalMeasurementEditDate, setIsOpenModalMeasurementEditDate] =
      useState<boolean>(false)
    const [isOpenModalCancel, setIsOpenModalCancel] = useState<boolean>(false)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const visibleFormData = useSelector(getVisibleFormData)
    const measurementFormData = useSelector(getMeasurementFormData)
    const parentId = useSelector(getMeasurementParentId)

    const handleNextStageOnClick = useCallback(async () => {
      if (toOrder) {
        await toOrder?.()
      }
    }, [toOrder])

    const onCloseAddNote = useCallback(() => {
      setIsOpenModalAddNote(false)
    }, [setIsOpenModalAddNote])

    const onCloseCreateTask = useCallback(() => {
      setIsOpenModalCreateTask(false)
    }, [setIsOpenModalCreateTask])

    const onCloseMeasurementEditDateModal = useCallback(() => {
      setIsOpenModalMeasurementEditDate(false)
    }, [setIsOpenModalMeasurementEditDate])

    const handleFinishOnClick = useCallback(async () => {
      if (toFinish) {
        await toFinish?.()
      }
    }, [toFinish])

    const handleCancelOnClick = useCallback(() => {
      if (visibleFormData) {
        const isRequiredFields = isFormDataRequired(
          visibleFormData?.resultObject,
          visibleFormData?.resultSchema,
        )
        if (!isRequiredFields) {
          Modal.warning({
            title: 'Предупреждение',
            content: 'Заполните все обязательные поля',
          })
          return
        }
      }

      setIsOpenModalCancel(true)
    }, [setIsOpenModalCancel, visibleFormData])

    const onCloseCancel = useCallback(() => {
      setIsOpenModalCancel(false)
    }, [setIsOpenModalCancel])

    const onCancel = useCallback(async () => {
      const result = await dispatch(getMeasurementForm(id))
      if (typeof result === 'string') {
        navigate(result)
        return
      }
      dispatch(getTimeLineData('case', parentId))
      setIsOpenModalCancel(false)
    }, [id, parentId, setIsOpenModalCancel, dispatch, navigate])

    const actionsConfig = useMemo(
      (): CardActionConfig[] => [
        {
          key: 'next-stage',
          label: titlesButton[stage] || '',
          type: 'primary',
          onClick: handleNextStageOnClick,
          isPrimary: true,
          condition: Boolean(titlesButton[stage]),
          order: 1,
        },
        {
          key: 'add-note',
          label: 'Добавить заметку',
          icon: <EditOutlined />,
          onClick: () => setIsOpenModalAddNote(true),
          condition: !isDisabledButtons,
          order: 10,
        },
        {
          key: 'create-task',
          label: 'Создать задачу',
          icon: <PlusOutlined />,
          onClick: () => setIsOpenModalCreateTask(true),
          condition: !isDisabledButtons,
          order: 20,
        },
        {
          key: 'edit-date',
          label: 'Изменить дату',
          icon: <CalendarOutlined />,
          onClick: () => setIsOpenModalMeasurementEditDate(true),
          condition: stage === 1,
          order: 30,
        },
        {
          key: 'finish',
          label: 'Выполнено',
          icon: <CheckOutlined />,
          onClick: handleFinishOnClick,
          condition: stage === 3,
          order: 40,
        },
        {
          key: 'cancel',
          label: 'Отмена',
          icon: <CloseOutlined />,
          danger: true,
          onClick: handleCancelOnClick,
          disabled: stage === 5,
          condition: !isDisabledButtons,
          order: 100,
        },
      ],
      [stage, isDisabledButtons, handleNextStageOnClick, handleFinishOnClick, handleCancelOnClick],
    )

    const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

    return (
      <>
        <HStack gap={'8'} className={'mapWrapper'} wrap>
          <div>
            {measurementFormData?.stages && (
              <HSteps
                items={Object.values(measurementFormData?.stages?.items)}
                current={measurementFormData?.stages?.current}
                history={measurementFormData?.stages?.history}
              />
            )}
          </div>
          <CardActions primaryAction={primaryAction} secondaryActions={secondaryActions} />
        </HStack>
        <Suspense fallback={''}>
          <ModalCreateTask
            isOpen={isOpenModalCreateTask}
            onClose={onCloseCreateTask}
            entityType={'case'}
            entityId={parentId}
          />
        </Suspense>
        <ModalAddNote
          entityId={Number(parentId)}
          entityType={'case'}
          isOpen={isOpenModalAddNote}
          onClose={onCloseAddNote}
        />
        {stage === 1 && (
          <MeasurementEditDateModal
            id={id}
            isModalOpen={isOpenModalMeasurementEditDate}
            onCancel={onCloseMeasurementEditDateModal}
          />
        )}
        {!isDisabledButtons && (
          <Suspense fallback={''}>
            <ModalCancel
              entityId={id}
              entityType="measurement"
              isOpen={isOpenModalCancel}
              onClose={onCloseCancel}
              onCancel={onCancel}
            />
          </Suspense>
        )}
      </>
    )
  },
)

MeasurementFormsHeader.displayName = 'MeasurementFormsHeader'
